import React, {
  ReactNode,
  useCallback,
  useMemo,
  useRef,
  useState,
  useLayoutEffect,
  useEffect,
} from 'react';
import {
  useHasSharedIntersectionObserver,
  useIntersectionObserverCallback,
} from '@client/hooks/use-intersection-observer.hooks';
import { useCollapseContext } from './ElementList/collapse.context';
import { usePrevious } from '@client/hooks/use-utils.hooks';
import { getHideOutsideViewportSlotStyle } from './hide-outside-viewport.helpers';

const checkVisibility = (element: HTMLElement) => {
  const rect = element.getBoundingClientRect();
  return (
    rect.top < window.innerHeight &&
    rect.bottom > 0 &&
    rect.left < window.innerWidth &&
    rect.right > 0
  );
};

type HideOutsideViewportProps = {
  children?: ReactNode;
  width?: string | number;
  height?: string | number;

  /**
   * Pass true to turn off the intersection observer and always render the children
   */
  alwaysVisible?: boolean;

  /**
   * Start with the children unmounted and only reveal the ones that are in the
   * viewport, decided by the pre-paint {@link syncCheckVisibility} measurement
   * (the placeholder already carries {@link height}, so its position is known
   * without mounting children). Use for large flat lists where the default
   * "mount everything, then cull" first render is too expensive. Only valid
   * when there is no {@link CollapseProvider} animating the row in (status
   * `entered`), otherwise the row stays blank during the expand animation.
   */
  cullBeforeMount?: boolean;

  /**
   * Toggle this to scroll to the component and show it's content
   */
  scrollTo?: boolean;
};

export const HideOutsideViewport: React.FC<HideOutsideViewportProps> = ({
  children,
  alwaysVisible,
  cullBeforeMount = false,
  width = '100%',
  height = 1,
  scrollTo = false,
}) => {
  const { status: collapseStatus } = useCollapseContext();
  const elementRef = useRef<HTMLDivElement | null>(null);
  const prevScrollTo = usePrevious<boolean>(scrollTo);

  // In prod a missing provider yields no observer; render everything rather
  // than leave rows stuck unmounted. In dev the missing provider throws.
  const hasObserver = useHasSharedIntersectionObserver();

  // With cullBeforeMount the children start unmounted; the pre-paint layout
  // effect below reveals only the rows actually in the viewport, so an
  // expand never pays the mount cost of the off-screen rows.
  const [isVisible, setIsVisible] = useState(
    cullBeforeMount ? false : collapseStatus === 'entered',
  );
  const style = useMemo(
    () =>
      getHideOutsideViewportSlotStyle({
        width,
        height,
        cullBeforeMount,
      }),
    [width, height, cullBeforeMount],
  );

  const shouldObserve =
    hasObserver &&
    (collapseStatus === 'entered' || collapseStatus === 'entering') &&
    alwaysVisible !== true;

  const syncCheckVisibility = useCallback(() => {
    if (!elementRef.current) return;
    setIsVisible(checkVisibility(elementRef.current));
  }, [elementRef]);

  useEffect(() => {
    const trigger = scrollTo && prevScrollTo === false;
    if (!trigger) return;

    setTimeout(() => {
      if (!elementRef.current) return; // if element is not mounted, don't scroll
      if (checkVisibility(elementRef.current)) return; // if already visible, don't scroll
      elementRef.current.scrollIntoView({
        behavior: 'auto',
        block: 'end',
      });
    }, 100);
  }, [scrollTo, prevScrollTo]);

  const intersectionChange = useCallback((entry: IntersectionObserverEntry) => {
    const visible = !!entry?.isIntersecting;
    setIsVisible(visible);
  }, []);

  // Check visibility synchronously when we start observing to prevent flicker
  useLayoutEffect(() => {
    if (collapseStatus === 'entered' || cullBeforeMount) syncCheckVisibility();
  }, [collapseStatus, cullBeforeMount, syncCheckVisibility]);

  useIntersectionObserverCallback(
    elementRef,
    shouldObserve ? intersectionChange : undefined,
  );

  const show = (() => {
    if (alwaysVisible || !hasObserver) return true;
    switch (collapseStatus) {
      case 'entered':
        return isVisible;
      case 'entering':
      case 'exiting':
      case 'exited':
        return true;
    }
  })();

  return (
    <div style={style} ref={elementRef}>
      {show ? children : null}
    </div>
  );
};
